import { useInfiniteQuery } from "@tanstack/react-query";
import { useContext } from "react";
import socratesApi from "../../../api/socratesApi";
import { MyContext } from "../../../context/MyContext";

/**
 * useSocratesV2InterviewListQuery
 *
 * Socrates V2 전용 면접 목록 조회 훅
 * 새로운 RESTful API (socratesApi.getUserInterviews)를 사용
 *
 * 기능:
 * - 무한 스크롤 지원 (클라이언트 측 페이지네이션)
 * - 캐싱 및 중복 방지
 * - 새 API 응답을 기존 형식에 맞게 변환
 */

interface SocratesInterview {
  id: number;
  title: string;
  updated_at: string;
  is_completed: boolean;
  isClicked: boolean;
  isEdit: boolean;
}

interface InterviewPage {
  interviews: SocratesInterview[];
  nextPage: number;
  hasMore: boolean;
}

interface UseSocratesV2InterviewListQueryOptions {
  limit?: number;
}

export const useSocratesV2InterviewListQuery = (
  options: UseSocratesV2InterviewListQueryOptions = {}
) => {
  const { limit = 50 } = options;
  const { user } = useContext(MyContext);
  const userId = user?.id;

  return useInfiniteQuery<InterviewPage>({
    queryKey: ["socratesV2", "interviews", "infinite", userId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!userId) {
        return { interviews: [], nextPage: 1, hasMore: false };
      }

      const rawInterviews = await socratesApi.getUserInterviews(userId);

      // 새 API 응답을 기존 형식에 맞게 변환
      // API 응답에서 id 필드가 다양한 이름일 수 있음 (id, interview_id, interviewId 등)
      const allInterviews = (rawInterviews || [])
        .filter((item: any) => item && (item.id || item.interview_id || item.interviewId))
        .map((item: any) => {
          const itemId = item.id || item.interview_id || item.interviewId;

          // 제목 형식: "면접유형 - 관심영역" (기존 버전과 동일)
          // interviewType이 있으면 해당 형식 사용, 없으면 title 또는 기본값 사용
          const interviewType = item.interviewType || item.interview_type;
          const interviewSubject = item.interviewSubject || item.interview_subject;

          let title: string;
          if (interviewType) {
            title = interviewSubject
              ? `${interviewType} - ${interviewSubject}`
              : interviewType;
          } else if (item.title) {
            title = item.title;
          } else {
            title = `면접 ${itemId}`;
          }

          return {
            id: itemId,
            title,
            updated_at: item.updatedAt || item.updated_at || item.createdAt || item.created_at || new Date().toISOString(),
            is_completed: item.status === "completed" || item.is_completed === true,
            isClicked: false,
            isEdit: false,
          };
        });

      // 클라이언트 측 페이지네이션
      const startIdx = ((pageParam as number) - 1) * limit;
      const endIdx = startIdx + limit;
      const pagedInterviews = allInterviews.slice(startIdx, endIdx);
      const hasMore = endIdx < allInterviews.length;

      return {
        interviews: pagedInterviews,
        nextPage: (pageParam as number) + 1,
        hasMore,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextPage : undefined,
    initialPageParam: 1,
    staleTime: 30000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    enabled: !!userId,
  });
};

// 편의를 위한 그룹 형식 변환 함수 (기존 컴포넌트 호환용)
export const convertToGroupFormat = (interviews: SocratesInterview[]) => {
  return interviews.map((interview) => ({
    id: interview.id,
    title: interview.title,
    updated_at: interview.updated_at,
    is_completed: interview.is_completed,
    isClicked: interview.isClicked,
    isEdit: interview.isEdit,
  }));
};
